import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, useSearchParams, Link, useLocation } from 'react-router-dom';
import {
  Typography,
  Box,
  CircularProgress,
  Button,
  Alert,
  TextField,
  Divider,
  Stack,
} from '@mui/material';
import { CheckCircle as CheckCircleIcon, Error as ErrorIcon, VpnKey as VpnKeyIcon } from '@mui/icons-material';
import { useLanguage } from '../../contexts/LanguageContext';
import { apiClient } from '../../services/api';
import { ApiError } from '../../services/api/http';
import AuthPageShell from './AuthPageShell';

interface VerificationLocationState {
  email?: string;
  message?: string;
}

const EmailVerification: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState<'loading' | 'success' | 'error' | 'manual'>('loading');
  const [message, setMessage] = useState<string>('');
  const [manualMessageError, setManualMessageError] = useState(false);
  const [verificationCode, setVerificationCode] = useState<string>('');
  const [email, setEmail] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [retryAfterSeconds, setRetryAfterSeconds] = useState<number | null>(null);
  const [isThrottledMessage, setIsThrottledMessage] = useState(false);
  const hasVerifiedRef = useRef(false);
  const cooldownStartedRef = useRef(false);
  const token = searchParams.get('token');
  const queryEmail = searchParams.get('email') || '';

  const locationState = location.state as VerificationLocationState | null;
  const stateEmail = locationState?.email || '';
  const stateMessage = locationState?.message || '';
  const isResendCooldownActive = retryAfterSeconds !== null;

  useEffect(() => {
    if (!isResendCooldownActive) {
      return undefined;
    }

    const intervalId = window.setInterval(() => {
      setRetryAfterSeconds(current => {
        if (current === null || current <= 1) {
          return null;
        }
        return current - 1;
      });
    }, 1000);

    return () => window.clearInterval(intervalId);
  }, [isResendCooldownActive]);

  useEffect(() => {
    if (isResendCooldownActive) {
      cooldownStartedRef.current = true;
      return;
    }

    if (cooldownStartedRef.current) {
      cooldownStartedRef.current = false;
      if (isThrottledMessage) {
        setIsThrottledMessage(false);
        setMessage(t('auth.enterEmailAndCode'));
        setManualMessageError(false);
      }
    }
  }, [isResendCooldownActive, isThrottledMessage, t]);

  const redirectToLogin = React.useCallback(() => {
    navigate('/login', {
      state: {
        message: t('auth.emailVerificationSuccessShort'),
      },
    });
  }, [navigate, t]);

  const verifyEmail = React.useCallback(async (verificationToken: string) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/auth/verify-email?token=${encodeURIComponent(verificationToken)}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || t('auth.emailVerifiedMessage'));
        setTimeout(redirectToLogin, 3000);
      } else {
        setStatus('error');
        setMessage(data.message || t('auth.verificationFailed'));
      }
    } catch {
      setStatus('error');
      setMessage(t('auth.verificationFailed'));
    }
  }, [t, redirectToLogin]);

  useEffect(() => {
    if (stateEmail || queryEmail) {
      setEmail(stateEmail || queryEmail);
    }
    if (stateMessage) {
      setMessage(stateMessage);
    }
  }, [stateEmail, stateMessage, queryEmail]);

  useEffect(() => {
    if (!token) {
      setStatus('manual');
      if (!message && !stateMessage) {
        setMessage(t('auth.enterEmailAndCode'));
      }
      return;
    }

    if (hasVerifiedRef.current) {
      return;
    }

    hasVerifiedRef.current = true;
    verifyEmail(token);
  }, [token, verifyEmail, t, message, stateMessage]);

  const handleVerifyWithCode = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!email || !verificationCode) {
      setMessage(t('auth.enterEmailAndCodeError'));
      setManualMessageError(true);
      return;
    }

    if (verificationCode.length !== 6) {
      setMessage(t('auth.verificationCodeLength'));
      setManualMessageError(true);
      return;
    }

    setIsSubmitting(true);
    
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080'}/auth/verify-email-code?email=${encodeURIComponent(email)}&code=${encodeURIComponent(verificationCode)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );

      const data = await response.json();

      if (response.ok) {
        setStatus('success');
        setMessage(data.message || t('auth.emailVerifiedMessage'));
        setTimeout(redirectToLogin, 3000);
      } else {
        setStatus('error');
        setMessage(data.message || t('auth.verificationCodeFailed'));
      }
    } catch {
      setMessage(t('auth.verificationFailed'));
      setManualMessageError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleResendVerification = async () => {
    if (!email) {
      setMessage(t('auth.enterEmailAddress'));
      setManualMessageError(true);
      return;
    }

    setIsSubmitting(true);
    
    try {
      await apiClient.resendVerification(email);
      setRetryAfterSeconds(null);
      setIsThrottledMessage(false);
      setMessage(t('auth.verificationResent'));
      setManualMessageError(false);
    } catch (error: unknown) {
      if (error instanceof ApiError && error.status === 429) {
        setRetryAfterSeconds(
          error.retryAfterSeconds && error.retryAfterSeconds > 0
            ? error.retryAfterSeconds
            : null,
        );
        setIsThrottledMessage(true);
        setMessage(t('auth.verificationThrottled'));
      } else {
        setRetryAfterSeconds(null);
        setIsThrottledMessage(false);
        setMessage(t('auth.verificationResendFailed'));
      }
      setManualMessageError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  const manualAlertMessage = retryAfterSeconds !== null
    ? `${t('auth.verificationThrottled')} ${t('auth.verificationRetryAfter')
      .replace('{{seconds}}', String(retryAfterSeconds))}`
    : message;

  return (
    <AuthPageShell title={t('auth.emailVerification')} description={t('auth.registrationNextStep')} icon={<VpnKeyIcon />}>

          {status === 'loading' && (
            <Box sx={{ mt: 4, mb: 4 }}>
              <CircularProgress size={60} />
              <Typography variant="body1" sx={{ mt: 2 }}>
                {t('auth.verifyingEmail')}
              </Typography>
            </Box>
          )}

          {status === 'success' && (
            <Box sx={{ mt: 4, mb: 4 }}>
              <CheckCircleIcon sx={{ fontSize: 80, color: 'success.main' }} />
              <Typography variant="h5" sx={{ mt: 2, mb: 2, color: 'success.main' }}>
                {t('auth.emailVerified')}
              </Typography>
              <Alert severity="success" sx={{ mt: 2 }}>
                {message}
              </Alert>
              <Typography variant="body2" sx={{ mt: 2 }}>
                {t('auth.redirectingToLogin')}
              </Typography>
            </Box>
          )}

          {status === 'manual' && (
            <Box sx={{ mt: 4, mb: 4 }}>
              <VpnKeyIcon sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" sx={{ mb: 3 }}>
                {t('auth.verifyYourEmail')}
              </Typography>
              
              {message && (
                <Alert severity={manualMessageError ? 'error' : 'info'} sx={{ mb: 3 }}>
                  {manualAlertMessage}
                </Alert>
              )}

              <Box component="form" onSubmit={handleVerifyWithCode} noValidate>
                <Stack spacing={2}>
                  <TextField
                    fullWidth
                    label={t('auth.emailAddress')}
                    type="email"
                    value={email}
                    onChange={(e) => {
                      setEmail(e.target.value);
                      setRetryAfterSeconds(null);
                      setIsThrottledMessage(false);
                      setMessage(t('auth.enterEmailAndCode'));
                      setManualMessageError(false);
                    }}
                    autoComplete="email"
                    InputProps={{
                      readOnly: !!stateEmail,
                    }}
                    sx={{
                      '& .MuiInputBase-input': {
                        backgroundColor: stateEmail ? 'action.hover' : 'transparent',
                      }
                    }}
                  />
                  
                  <TextField
                    fullWidth
                    label={t('auth.verificationCodeLabel')}
                    value={verificationCode}
                    onChange={(e) => {
                      const value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase();
                      if (value.length <= 6) {
                        setVerificationCode(value);
                        setManualMessageError(false);
                      }
                    }}
                    autoFocus={!!stateEmail}
                    inputProps={{
                      maxLength: 6,
                      style: { textTransform: 'uppercase', letterSpacing: '0.5em', fontSize: '1.2rem', textAlign: 'center' }
                    }}
                    helperText={t('auth.verificationCodeHelper')}
                    placeholder="ABC123"
                  />

                  <Button
                    type="submit"
                    fullWidth
                    variant="contained"
                    size="large"
                    disabled={isSubmitting || verificationCode.length !== 6}
                    sx={{ mt: 2 }}
                  >
                    {isSubmitting ? <CircularProgress size={24} /> : t('auth.verifyEmailButton')}
                  </Button>
                </Stack>
              </Box>

              <Divider sx={{ my: 3 }}>{t('auth.or')}</Divider>

              <Button
                fullWidth
                variant="outlined"
                onClick={handleResendVerification}
                disabled={isSubmitting || !email || isResendCooldownActive}
                sx={{ mb: 2 }}
              >
                {t('auth.resendVerificationCode')}
              </Button>

              <Button
                component={Link}
                to="/login"
                fullWidth
                variant="text"
                sx={{ mt: 1 }}
              >
                {t('auth.backToLogin')}
              </Button>
            </Box>
          )}

          {status === 'error' && (
            <Box sx={{ mt: 4, mb: 4 }}>
              <ErrorIcon sx={{ fontSize: 80, color: 'error.main' }} />
              <Typography variant="h5" sx={{ mt: 2, mb: 2, color: 'error.main' }}>
                {t('auth.verificationFailed')}
              </Typography>
              <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
                {message}
              </Alert>
              <Stack spacing={2} sx={{ mt: 3 }}>
                <Button
                  variant="contained"
                  onClick={() => {
                    setStatus('manual');
                    setMessage(t('auth.enterEmailAndCode'));
                    setManualMessageError(false);
                  }}
                >
                  {t('auth.tryAgainWithCode')}
                </Button>
                <Button
                  component={Link}
                  to="/login"
                  variant="outlined"
                >
                  {t('auth.goToLogin')}
                </Button>
              </Stack>
            </Box>
          )}
    </AuthPageShell>
  );
};

export default EmailVerification;
